import React, { useState, useEffect, useRef, useCallback } from 'react';
import StreamingText from './StreamingText';
import FlowConnector from './FlowConnector';
import MarketCards from './MarketCards';
import './ProcessingJourney.css';

/**
 * ProcessingJourney - Cinematic visualization of the AI processing pipeline
 */
const ProcessingJourney = ({ kernelId, kernel, onViewResults }) => {
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prevKernelRef = useRef(null);

  // Track which stages have completed their animation/streaming
  const [completedStages, setCompletedStages] = useState({
    intake: false,
    public: false,
    markets: false,
    aggregation: [false, false, false],
    publicProjection: false,
    privateProjection: false,
  });

  // Track when we first saw each piece of data (null = not seen, timestamp = when seen)
  const firstSeenRef = useRef({
    intake: null,
    public: null,
    aggregation: [null, null, null],
    publicProjection: null,
    privateProjection: null,
  });

  // Mark a stage as complete
  const completeStage = useCallback((stage, index = null) => {
    setCompletedStages((prev) => {
      if (index !== null) {
        const newAgg = [...prev.aggregation];
        newAgg[index] = true;
        return { ...prev, aggregation: newAgg };
      }
      return { ...prev, [stage]: true };
    });
  }, []);

  // Track if user is near bottom of the page
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      // Consider "near bottom" if within 150px of bottom
      isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 150;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll only when new data arrives AND user is near bottom
  useEffect(() => {
    // Check if kernel data actually changed (new content arrived)
    const prevKernel = prevKernelRef.current;
    const hasNewContent =
      !prevKernel ||
      kernel?.intake_agent_thoughts !== prevKernel?.intake_agent_thoughts ||
      kernel?.public_agent_thoughts !== prevKernel?.public_agent_thoughts ||
      kernel?.affected_markets?.length !== prevKernel?.affected_markets?.length ||
      kernel?.aggregation_questions?.length !== prevKernel?.aggregation_questions?.length ||
      JSON.stringify(kernel?.projection) !== JSON.stringify(prevKernel?.projection) ||
      kernel?.status !== prevKernel?.status;

    prevKernelRef.current = kernel;

    // Only scroll if there's new content AND user is near bottom
    if (hasNewContent && isNearBottomRef.current) {
      const timer = setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [kernel]);

  // Extract data from kernel
  const intakeText = kernel?.intake_agent_thoughts;
  const publicText = kernel?.public_agent_thoughts;
  const searchQuestion = kernel?.search_question;
  const isRejected = kernel?.public_agent_decision === 'No';
  const markets = kernel?.affected_markets;
  const selectedMarket = kernel?.selected_market;
  const aggregationQuestions = kernel?.aggregation_questions;
  const projection = kernel?.projection;
  const isComplete = kernel?.status === 'projection_complete';

  // Track first-seen timestamps for streaming decisions
  const now = Date.now();

  // Check if data just arrived (within last 500ms) - if so, stream it
  const shouldStreamIntake = (() => {
    if (!intakeText) return false;
    if (completedStages.intake) return false;
    if (!firstSeenRef.current.intake) {
      firstSeenRef.current.intake = now;
      return true; // Just arrived, stream it
    }
    return now - firstSeenRef.current.intake < 500; // Still within streaming window
  })();

  const shouldStreamPublic = (() => {
    if (!publicText) return false;
    if (completedStages.public) return false;
    if (!firstSeenRef.current.public) {
      firstSeenRef.current.public = now;
      return true;
    }
    return now - firstSeenRef.current.public < 500;
  })();

  // Helper for aggregation questions
  const shouldStreamAggregation = (index, text) => {
    if (!text) return false;
    if (completedStages.aggregation[index]) return false;
    if (!firstSeenRef.current.aggregation[index]) {
      firstSeenRef.current.aggregation[index] = now;
      return true;
    }
    return now - firstSeenRef.current.aggregation[index] < 500;
  };

  const shouldStreamPublicProjection = (() => {
    const text = projection?.publicPricing?.reasoning;
    if (!text) return false;
    if (completedStages.publicProjection) return false;
    if (!firstSeenRef.current.publicProjection) {
      firstSeenRef.current.publicProjection = now;
      return true;
    }
    return now - firstSeenRef.current.publicProjection < 500;
  })();

  const shouldStreamPrivateProjection = (() => {
    const text = projection?.privatePricing?.reasoning;
    if (!text) return false;
    if (completedStages.privateProjection) return false;
    if (!firstSeenRef.current.privateProjection) {
      firstSeenRef.current.privateProjection = now;
      return true;
    }
    return now - firstSeenRef.current.privateProjection < 500;
  })();

  // Auto-complete stages that have data but aren't streaming
  useEffect(() => {
    if (intakeText && !shouldStreamIntake && !completedStages.intake) {
      const timer = setTimeout(() => completeStage('intake'), 100);
      return () => clearTimeout(timer);
    }
  }, [intakeText, shouldStreamIntake, completedStages.intake, completeStage]);

  useEffect(() => {
    if (publicText && !shouldStreamPublic && !completedStages.public) {
      const timer = setTimeout(() => completeStage('public'), 100);
      return () => clearTimeout(timer);
    }
  }, [publicText, shouldStreamPublic, completedStages.public, completeStage]);

  // Determine if stages should show (based on prior stage completion)
  const showStage2 = completedStages.intake;
  const showStage3 = completedStages.public && !isRejected;
  const showStage4 = completedStages.markets;
  const showStage5 = completedStages.aggregation.every(Boolean) && aggregationQuestions?.length > 0;
  const showStage6 = isComplete && completedStages.publicProjection && completedStages.privateProjection;

  // Auto-complete markets stage when data arrives (after animation time)
  useEffect(() => {
    if (markets?.length > 0 && selectedMarket && showStage3 && !completedStages.markets) {
      const timer = setTimeout(() => {
        completeStage('markets');
      }, Math.min(markets.length, 8) * 200 + 1200);
      return () => clearTimeout(timer);
    }
  }, [markets, selectedMarket, showStage3, completedStages.markets, completeStage]);

  // Auto-complete aggregation stages that aren't streaming
  useEffect(() => {
    if (!aggregationQuestions?.length) return;

    aggregationQuestions.slice(0, 3).forEach((qa, index) => {
      if (qa.answer && !shouldStreamAggregation(index, qa.answer) && !completedStages.aggregation[index]) {
        setTimeout(() => completeStage('aggregation', index), 100 + index * 50);
      }
    });
  }, [aggregationQuestions, completedStages.aggregation, completeStage]);

  // Auto-complete projection stages that aren't streaming
  useEffect(() => {
    if (projection?.publicPricing?.reasoning && !shouldStreamPublicProjection && !completedStages.publicProjection) {
      const timer = setTimeout(() => completeStage('publicProjection'), 100);
      return () => clearTimeout(timer);
    }
  }, [projection?.publicPricing?.reasoning, shouldStreamPublicProjection, completedStages.publicProjection, completeStage]);

  useEffect(() => {
    if (projection?.privatePricing?.reasoning && !shouldStreamPrivateProjection && !completedStages.privateProjection) {
      const timer = setTimeout(() => completeStage('privateProjection'), 100);
      return () => clearTimeout(timer);
    }
  }, [projection?.privatePricing?.reasoning, shouldStreamPrivateProjection, completedStages.privateProjection, completeStage]);

  return (
    <div className="processing-journey">
      <div className="processing-journey__container">
        {/* Header */}
        <div className="processing-journey__header">
          <span className="processing-journey__kernel-id">Kernel: {kernelId}</span>
        </div>

        {/* Stage 1: Intake Assessment */}
        <div className="journey-stage journey-stage--visible">
          <div className="journey-stage__box">
            <h3 className="journey-stage__title">
              Assessing if the user is an expert and their belief is worth investigating further.
            </h3>
            {intakeText ? (
              <div className="journey-stage__content">
                {shouldStreamIntake ? (
                  <StreamingText
                    key="intake-stream"
                    text={intakeText}
                    speed={15}
                    onComplete={() => completeStage('intake')}
                  />
                ) : (
                  <span>{intakeText}</span>
                )}
              </div>
            ) : (
              <div className="journey-stage__loading">
                <span className="journey-stage__loading-dot" />
                <span className="journey-stage__loading-dot" />
                <span className="journey-stage__loading-dot" />
              </div>
            )}
          </div>
        </div>

        {/* Connector 1→2 */}
        {showStage2 && <FlowConnector animated />}

        {/* Stage 2: Public Information Check */}
        {showStage2 && (
          <div className="journey-stage journey-stage--visible">
            <div className="journey-stage__box">
              <h3 className="journey-stage__title">
                Cross-checking novelty of belief with public information
              </h3>
              {searchQuestion && (
                <div className="journey-stage__subtitle">
                  <span className="journey-stage__label">Research Question:</span>
                  <p className="journey-stage__question">{searchQuestion}</p>
                </div>
              )}
              {publicText ? (
                <div className="journey-stage__content">
                  {shouldStreamPublic ? (
                    <StreamingText
                      key="public-stream"
                      text={publicText}
                      speed={15}
                      onComplete={() => completeStage('public')}
                    />
                  ) : (
                    <span>{publicText}</span>
                  )}
                </div>
              ) : (
                <div className="journey-stage__loading">
                  <span className="journey-stage__loading-dot" />
                  <span className="journey-stage__loading-dot" />
                  <span className="journey-stage__loading-dot" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rejection State */}
        {completedStages.public && isRejected && (
          <>
            <FlowConnector animated />
            <div className="journey-stage journey-stage--visible">
              <div className="journey-stage__box journey-stage__box--rejected">
                <div className="journey-stage__rejected-icon">X</div>
                <h3 className="journey-stage__title journey-stage__title--rejected">
                  Information Not Accepted
                </h3>
                <p className="journey-stage__rejected-text">
                  This belief appears to be redundant with publicly available information.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Connector 2→3 */}
        {showStage3 && <FlowConnector animated />}

        {/* Stage 3: Market Discovery - 1.2x wider */}
        {showStage3 && (
          <div className="journey-stage journey-stage--visible journey-stage--wide-sm">
            <div className="journey-stage__box">
              <h3 className="journey-stage__title">
                This belief is worth investigating. Finding related markets.
              </h3>
              {markets?.length > 0 ? (
                <MarketCards
                  markets={markets}
                  selectedId={selectedMarket?.conditionId || selectedMarket?.id}
                  onAnimationComplete={() => completeStage('markets')}
                />
              ) : (
                <div className="journey-stage__loading">
                  <span className="journey-stage__loading-dot" />
                  <span className="journey-stage__loading-dot" />
                  <span className="journey-stage__loading-dot" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Connector 3→4 */}
        {showStage4 && <FlowConnector animated />}

        {/* Stage 4: Aggregation Questions */}
        {showStage4 && (
          <>
            {/* Title box - normal width */}
            <div className="journey-stage journey-stage--visible">
              <div className="journey-stage__box">
                <h3 className="journey-stage__title journey-stage__title--no-margin">
                  Analyzing this underlying market and building a set of public information regarding it
                </h3>
              </div>
            </div>

            <FlowConnector animated height="24px" />

            {/* Questions grid - 1.5x wider */}
            <div className="journey-stage journey-stage--visible journey-stage--wide-lg">
              <div className="journey-stage__aggregation-grid">
                {[0, 1, 2].map((index) => {
                  const qa = aggregationQuestions?.[index];
                  const shouldStream = qa?.answer ? shouldStreamAggregation(index, qa.answer) : false;
                  return (
                    <div key={index} className="aggregation-box">
                      {qa?.question ? (
                        <p className="aggregation-box__question">{qa.question}</p>
                      ) : (
                        <p className="aggregation-box__question aggregation-box__question--loading">
                          Generating question...
                        </p>
                      )}
                      <div className="aggregation-box__answer">
                        {qa?.answer ? (
                          shouldStream ? (
                            <StreamingText
                              key={`agg-${index}-stream`}
                              text={qa.answer}
                              speed={10}
                              onComplete={() => completeStage('aggregation', index)}
                            />
                          ) : (
                            <span>{qa.answer}</span>
                          )
                        ) : (
                          <span className="aggregation-box__loading">
                            {qa?.question ? 'Researching...' : 'Waiting...'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Connector 4→5 */}
        {showStage5 && <FlowConnector animated />}

        {/* Stage 5: Dual Projections - 1.2x wider */}
        {showStage5 && (
          <div className="journey-stage journey-stage--visible journey-stage--wide-sm">
            <div className="journey-stage__projections-row">
              {/* Public-Only Projection */}
              <div className="projection-box projection-box--public">
                <h4 className="projection-box__title">Public Forecast</h4>
                <p className="projection-box__subtitle">Analysis using only public information</p>
                {projection?.publicPricing ? (
                  <>
                    <div className="projection-box__value">
                      {((Number(projection.publicPricing.fairValueLow) +
                        Number(projection.publicPricing.fairValueHigh)) / 2 * 100
                      ).toFixed(1)}%
                    </div>
                    <div className="projection-box__range">
                      Range: {(projection.publicPricing.fairValueLow * 100).toFixed(0)}% -{' '}
                      {(projection.publicPricing.fairValueHigh * 100).toFixed(0)}%
                    </div>
                    <div className="projection-box__reasoning">
                      {shouldStreamPublicProjection ? (
                        <StreamingText
                          key="public-proj-stream"
                          text={projection.publicPricing.reasoning}
                          speed={10}
                          onComplete={() => completeStage('publicProjection')}
                        />
                      ) : (
                        <span>{projection.publicPricing.reasoning}</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="journey-stage__loading">
                    <span className="journey-stage__loading-dot" />
                    <span className="journey-stage__loading-dot" />
                    <span className="journey-stage__loading-dot" />
                  </div>
                )}
              </div>

              {/* Private-Info Projection */}
              <div className="projection-box projection-box--private">
                <h4 className="projection-box__title">With Your Information</h4>
                <p className="projection-box__subtitle">Analysis including your private belief</p>
                {projection?.privatePricing ? (
                  <>
                    <div className="projection-box__value">
                      {((Number(projection.privatePricing.fairValueLow) +
                        Number(projection.privatePricing.fairValueHigh)) / 2 * 100
                      ).toFixed(1)}%
                    </div>
                    <div className="projection-box__range">
                      Range: {(projection.privatePricing.fairValueLow * 100).toFixed(0)}% -{' '}
                      {(projection.privatePricing.fairValueHigh * 100).toFixed(0)}%
                    </div>
                    <div className="projection-box__reasoning">
                      {shouldStreamPrivateProjection ? (
                        <StreamingText
                          key="private-proj-stream"
                          text={projection.privatePricing.reasoning}
                          speed={10}
                          onComplete={() => completeStage('privateProjection')}
                        />
                      ) : (
                        <span>{projection.privatePricing.reasoning}</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="journey-stage__loading">
                    <span className="journey-stage__loading-dot" />
                    <span className="journey-stage__loading-dot" />
                    <span className="journey-stage__loading-dot" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stage 6: Complete - View Results Button */}
        {showStage6 && (
          <>
            <FlowConnector animated />
            <div className="journey-stage journey-stage--visible">
              <div className="journey-stage__complete">
                <h3 className="journey-stage__complete-title">Analysis Complete</h3>
                <p className="journey-stage__complete-text">
                  Your information has been processed and valued against the market.
                </p>
                <button className="journey-stage__view-btn" onClick={onViewResults}>
                  View Results
                </button>
              </div>
            </div>
          </>
        )}

        {/* Scroll anchor */}
        <div ref={bottomRef} className="processing-journey__bottom" />
      </div>
    </div>
  );
};

export default ProcessingJourney;
